import * as stylex from '@stylexjs/stylex'
import { type ComponentPropsWithoutRef, type ForwardedRef, type PropsWithChildren } from 'react'
import { useIsExited } from '../useIsExited/useIsExited'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const fadeIn = stylex.keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
})

const fadeOut = stylex.keyframes({
  '0%': { opacity: 1 },
  '100%': { opacity: 0 },
})

const dynamicStyles = stylex.create({
  duration: (duration: number) => ({
    animationDuration: `${duration}ms`,
  }),
})

const _baseStyles = stylex.create({
  fadeIn: {
    animationFillMode: 'forwards',
    animationName: fadeIn,
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  fadeOut: {
    animationFillMode: 'forwards',
    animationName: fadeOut,
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
})

export interface FadeProps extends ComponentPropsWithoutRef<'div'> {
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement>
  /** 控制淡入（true）或淡出（false），淡出動畫結束後會卸載 */
  in?: boolean
  /** 自訂根元素的 StyleX 樣式 */
  rootStyle?: stylex.StyleXStyles
  /** 覆蓋內部子樣式的 xStyles */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 動畫持續時間（毫秒）
   * @default 225
   */
  duration?: number
  /** 淡出動畫結束後的回呼 */
  onExited?: () => void
}

/**
 * Fade 是淡入淡出過渡動畫元件，透過 in prop 控制顯示與隱藏。
 * 淡出動畫結束後元件會自動卸載。
 */
export const Fade = (props: PropsWithChildren<FadeProps>) => {
  const { rootRef, in: inProp, rootStyle, xStyles, children, onAnimationStart, onAnimationEnd, onExited, duration = 225, ...rest } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)
  const { isExited, ...animationHandlers } = useIsExited({
    show: inProp,
    onAnimationStart,
    onAnimationEnd,
    onExited,
  })

  if (!inProp && isExited) return null

  return (
    <div
      ref={rootRef}
      {...stylex.props([
        inProp ? baseStyles.fadeIn : baseStyles.fadeOut,
        dynamicStyles.duration(duration),
        rootStyle,
      ])}
      {...animationHandlers}
      {...rest}
    >
      {children}
    </div>
  )
}
